/**
 * 个性化配置相关API
 */
import { request } from '@/utils/http'
import type {
  PersonalizationSaveRequest,
  PersonalizationConfigResponse,
  DomainVerifyResult,
  CompanyNewsItem,
  EnterpriseAdvantageCard
} from '@/types/personalization'

// API接口路径
const API_BASE = 'enterprise/enterprise-config'
const NEWS_API_BASE = 'enterprise/enterprise-company-news'
const ADVANTAGE_API_BASE = 'enterprise/enterprise-advantage-card'

/**
 * 公开接口：按域名获取企业配置（登录页等未登录场景使用）
 */
export interface EnterpriseConfigByDomain {
  /** 企业 ID（后端 Long 统一序列化为字符串） */
  enterpriseId?: string | number
  /** 租户 ID（后端 Long 统一序列化为字符串） */
  tenantId?: string | number
  /** 企业中文名称 */
  enterpriseName?: string
  /** 企业英文名称 */
  enterpriseNameEn?: string
  /** 系统图标文件 ID（浏览器标签页 favicon） */
  systemIconFile?: string
}

/**
 * 按域名获取企业个性化配置（公开接口，未登录可用）
 */
export function getEnterpriseConfigByDomain(domain: string) {
  return request.get<EnterpriseConfigByDomain | null>(
    `${API_BASE}/by-domain`,
    { domain },
    { showErrorMessage: false }
  )
}

/**
 * 公开接口：按域名下载企业品牌文件（返回可直接用于 img/favicon 的 data URL）
 * 未登录场景下不能走 /storage/findByFileId（需要 token），用这个域名白名单接口。
 */
export function getEnterpriseFileByDomain(domain: string, fileId: string) {
  return request.get<string>(
    `${API_BASE}/download-file-by-domain`,
    { domain, fileId },
    { showErrorMessage: false }
  )
}

/**
 * 获取个性化配置
 */
export function getPersonalizationConfig() {
  return request.get<PersonalizationConfigResponse>(`${API_BASE}/current`)
}

/**
 * 保存个性化配置
 */
export function savePersonalizationConfig(data: PersonalizationSaveRequest) {
  return request.post<boolean>(`${API_BASE}/save`, data)
}

/**
 * 更新个性化配置
 */
export function updatePersonalizationConfig(data: Record<string, unknown>) {
  return request.post<boolean>(`${API_BASE}/update`, data)
}

/**
 * 检查域名可用性（仅占用）
 */
export function checkDomainAvailability(domain: string) {
  return request.post<{ available: boolean }>(
    `${API_BASE}/check-domain?domain=${encodeURIComponent(domain)}`
  )
}

/**
 * 域名校验：占用 + 连通性（不做 DNS 配置检测），任一失败则 success=false
 */
export function verifyDomain(domain: string) {
  return request.post<DomainVerifyResult>(
    `${API_BASE}/verify-domain?domain=${encodeURIComponent(domain)}`
  )
}

/**
 * 上传Logo文件
 */
export function uploadLogo(file: File, type: 'header' | 'footer') {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('type', type)

  return request.post<{ url: string }>(`${API_BASE}/upload-logo`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
}

/**
 * 上传社交媒体二维码
 */
export function uploadSocialMediaQR(file: File, platform: string) {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('platform', platform)

  return request.post<{ url: string }>(`${API_BASE}/upload-social-qr`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
}

/**
 * 获取默认配置
 */
export function getDefaultPersonalizationConfig() {
  return request.get<PersonalizationConfigResponse>(`${API_BASE}/default-config`)
}

// ==================== 公司新闻 ====================

/** 获取企业新闻列表（管理端，含草稿） */
export function getCompanyNewsList() {
  return request.get<CompanyNewsItem[]>(`${NEWS_API_BASE}/list`)
}

/** 批量保存/更新企业新闻 */
export function saveCompanyNews(newsList: CompanyNewsItem[]) {
  return request.post<boolean>(`${NEWS_API_BASE}/save`, newsList)
}

/** 删除企业新闻 */
export function deleteCompanyNews(id: number) {
  return request.post<boolean>(`${NEWS_API_BASE}/delete?id=${id}`)
}

/** Family 企业档案（用于 Header Logo / 标语等来源预览） */
export interface FamilyEnterpriseProfile {
  enterpriseName?: string
  logo?: string
  aboutUs?: string
}

export function getFamilyProfile() {
  return request.post<FamilyEnterpriseProfile>(
    '/enterprise/tenant/clientEnterprise/getFamilyProfile'
  )
}

// ==================== 核心价值与优势卡片 ====================

/** 获取当前企业优势卡片列表 */
export function getAdvantageCardList() {
  return request.get<EnterpriseAdvantageCard[]>(`${ADVANTAGE_API_BASE}/list`)
}

/** 批量保存/更新优势卡片 */
export function saveAdvantageCards(cards: EnterpriseAdvantageCard[]) {
  return request.post<boolean>(`${ADVANTAGE_API_BASE}/save`, cards)
}

/** 删除优势卡片 */
export function deleteAdvantageCard(id: number) {
  return request.post<boolean>(`${ADVANTAGE_API_BASE}/delete?id=${id}`)
}

/** 企业服务能力（服务亮点 / 服务与覆盖预览） */
export interface EnterpriseCapabilityItem {
  id?: number
  serviceCategory?: string
  categoryTitle?: string
  serviceCategoryCn?: string
  serviceItem?: string
  code?: string
  name?: string
  nameCn?: string
  serviceType?: string
  locationCode?: string
  departureCode?: string
  destinationCode?: string
  location?: string
  departureLocation?: string
  destinationLocation?: string
  isHighlight?: boolean
}

/** 获取企业服务能力列表（企业档案） */
export function getCapabilityList() {
  return request.get<EnterpriseCapabilityItem[]>('/enterprise/capability/list')
}

export type { DomainVerifyResult, CompanyNewsItem, EnterpriseAdvantageCard }
